import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    InputAdornment,
    IconButton,
    Divider,
    Stack,
    Tab,
    Tabs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Person,
    Lock,
    Email,
    School,
    Business,
    Group,
    PhoneIphone,
    Save,
    AccountCircle,
} from '@mui/icons-material';
import PageContainer from '../components/layout/PageContainer';
import { getUserProfile, updateUserProfile, changePassword } from '../services/userService';
import { UserProfileData, UserUpdateRequest, PasswordChangeRequest } from '../types/user';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`account-tabpanel-${index}`}
            aria-labelledby={`account-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const Account: React.FC = () => {
    const [tabValue, setTabValue] = useState(0);
    const [userProfileData, setUserProfileData] = useState<UserProfileData | null>(null);
    const [profileFormData, setProfileFormData] = useState({
        first_name: '',
        last_name: '',
        university: '',
        endowment_name: '',
        activism_organization: '',
        signal_id: '',
    });
    const [passwordFormData, setPasswordFormData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [isPasswordLoading, setIsPasswordLoading] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    useEffect(() => {
        loadUserProfile();
    }, []);

    const loadUserProfile = async () => {
        try {
            const data = await getUserProfile();
            setUserProfileData(data);
            setProfileFormData({
                first_name: data.user.first_name || '',
                last_name: data.user.last_name || '',
                university: data.profile.university || '',
                endowment_name: data.profile.endowment_name || '',
                activism_organization: data.profile.activism_organization || '',
                signal_id: data.profile.signal_id || '',
            });
        } catch (error: any) {
            setProfileError(error.message || 'Failed to load profile');
        } finally {
            setIsInitialLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setProfileError('');
        setPasswordError('');
        setProfileSuccess('');
        setPasswordSuccess('');
    };

    const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setProfileFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPasswordFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsProfileLoading(true);
        setProfileError('');
        setProfileSuccess('');

        try {
            const updateData: UserUpdateRequest = {
                first_name: profileFormData.first_name,
                last_name: profileFormData.last_name,
                userprofile: {
                    university: profileFormData.university || undefined,
                    endowment_name: profileFormData.endowment_name || undefined,
                    activism_organization: profileFormData.activism_organization || undefined,
                    signal_id: profileFormData.signal_id || undefined,
                }
            };

            const response = await updateUserProfile(updateData);
            setProfileSuccess('Profile updated successfully!');
            
            // Reload profile data to reflect changes
            await loadUserProfile();
        } catch (error: any) {
            setProfileError(error.message || 'Failed to update profile');
        } finally {
            setIsProfileLoading(false);
        }
    };

    const handlePasswordSubmit = async () => {
        setIsPasswordLoading(true);
        setPasswordError('');
        setPasswordSuccess('');

        // Basic validation
        if (passwordFormData.new_password.length < 8) {
            setPasswordError('New password must be at least 8 characters long');
            setIsPasswordLoading(false);
            setConfirmDialogOpen(false);
            return;
        }

        if (passwordFormData.new_password !== passwordFormData.confirm_password) {
            setPasswordError('New passwords do not match');
            setIsPasswordLoading(false);
            setConfirmDialogOpen(false);
            return;
        }

        try {
            const passwordData: PasswordChangeRequest = {
                current_password: passwordFormData.current_password,
                new_password: passwordFormData.new_password,
                confirm_password: passwordFormData.confirm_password,
            };

            await changePassword(passwordData);
            setPasswordSuccess('Password changed successfully!');
            
            // Clear form
            setPasswordFormData({
                current_password: '',
                new_password: '',
                confirm_password: '',
            });
        } catch (error: any) {
            setPasswordError(error.message || 'Failed to change password');
        } finally {
            setIsPasswordLoading(false);
            setConfirmDialogOpen(false);
        }
    };

    const handlePasswordFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setConfirmDialogOpen(true);
    };

    if (isInitialLoading) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Typography>Loading profile...</Typography>
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
                    Account Settings
                </Typography>

                <Card>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="account tabs">
                            <Tab
                                label="Profile Information"
                                icon={<AccountCircle />}
                                iconPosition="start"
                                id="account-tab-0"
                                aria-controls="account-tabpanel-0"
                            />
                            <Tab
                                label="Change Password"
                                icon={<Lock />}
                                iconPosition="start"
                                id="account-tab-1"
                                aria-controls="account-tabpanel-1"
                            />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Profile Information
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Update your personal information. Note: Username and email cannot be changed.
                        </Typography>

                        <Box component="form" onSubmit={handleProfileSubmit}>
                            <Stack spacing={3} sx={{ maxWidth: 600 }}>
                                {/* Read-only fields */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Username"
                                        value={userProfileData?.user.username || ''}
                                        disabled
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person color="disabled" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        helperText="Username cannot be changed"
                                    />
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        value={userProfileData?.user.email || ''}
                                        disabled
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email color="disabled" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        helperText="Email cannot be changed"
                                    />
                                </Box>

                                {/* Editable user fields */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        name="first_name"
                                        label="First Name"
                                        value={profileFormData.first_name}
                                        onChange={handleProfileChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        name="last_name"
                                        label="Last Name"
                                        value={profileFormData.last_name}
                                        onChange={handleProfileChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {/* Profile fields */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        name="university"
                                        label="University"
                                        value={profileFormData.university}
                                        onChange={handleProfileChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <School color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        name="endowment_name"
                                        label="Endowment Name"
                                        value={profileFormData.endowment_name}
                                        onChange={handleProfileChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Business color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        name="activism_organization"
                                        label="Activism Organization"
                                        value={profileFormData.activism_organization}
                                        onChange={handleProfileChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Group color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        name="signal_id"
                                        label="Signal ID"
                                        value={profileFormData.signal_id}
                                        onChange={handleProfileChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIphone color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Stack>

                            {profileError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {profileError}
                                </Alert>
                            )}

                            {profileSuccess && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    {profileSuccess}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<Save />}
                                disabled={isProfileLoading}
                                sx={{ mt: 3 }}
                            >
                                {isProfileLoading ? 'Updating...' : 'Update Profile'}
                            </Button>
                        </Box>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Change Password
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Choose a strong password to keep your account secure.
                        </Typography>

                        <Box component="form" onSubmit={handlePasswordFormSubmit}>
                            <Stack spacing={3} sx={{ maxWidth: 400 }}>
                                <TextField
                                    required
                                    fullWidth
                                    name="current_password"
                                    label="Current Password"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={passwordFormData.current_password}
                                    onChange={handlePasswordChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle current password visibility"
                                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                    edge="end"
                                                >
                                                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    required
                                    fullWidth
                                    name="new_password"
                                    label="New Password"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={passwordFormData.new_password}
                                    onChange={handlePasswordChange}
                                    helperText="Minimum 8 characters"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle new password visibility"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    edge="end"
                                                >
                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    required
                                    fullWidth
                                    name="confirm_password"
                                    label="Confirm New Password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={passwordFormData.confirm_password}
                                    onChange={handlePasswordChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle confirm password visibility"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>

                            {passwordError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {passwordError}
                                </Alert>
                            )}

                            {passwordSuccess && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    {passwordSuccess}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<Lock />}
                                disabled={isPasswordLoading}
                                sx={{ mt: 3 }}
                            >
                                Change Password
                            </Button>
                        </Box>
                    </TabPanel>
                </Card>

                {/* Confirmation Dialog */}
                <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                    <DialogTitle>Confirm Password Change</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to change your password? You will need to use the new password to log in.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handlePasswordSubmit} variant="contained" disabled={isPasswordLoading}>
                            {isPasswordLoading ? 'Changing...' : 'Confirm Change'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PageContainer>
    );
};

export default Account;